import { ServiceError } from '../errors'
import { readLocal, writeLocal } from '../local-store'
import { MOCK_SLOW_MS, simulate } from '../mock-utils'
import { money } from '../types'
import type { StayRef } from '../types'
import { PAYMENT_POLL_MS, PAYMENT_WAIT_MS, sleep } from './flow'
import { FISCAL_FIELD } from './real'
import type { Charge, InStayConsent, InStayConsentInput, PagosService, PaymentKind, PaymentSummary, Receipt, TouristTax } from './types'

/**
 * Datos del bundle (Pagos.dc.html): Ático Plaza Mayor todo pagado (450 €) y Hotel Mirador del Puerto con el
 * 50 % pendiente y tasa turística. Códigos de reserva para ver estados (además de error/offline/lento-pagos):
 *  - `canal` → hay que validar la tarjeta del canal
 *  - `pago-directo` → el PMS cobra sin pasar por el banco · `pago-ya-pagado` · `pago-falla` (E3 no ok)
 *  - `pago-ok` / `pago-rechazado` / `pago-tiempo` → resultado automático del banco; sin ellos, se decide en
 *    la página simulada del banco (`/dev/banco`, solo en desarrollo)
 *  - `sin-cargos`, `sin-recibos`, `tasa-exentos`, `factura-falla`, `sin-tarjeta`
 *  - `consentimiento-pendiente` (como hoy en real), `consentimiento-falla`
 */
export const MOCK_PAYMENT_PAID: PaymentSummary = {
  total: money(45000),
  pending: money(0),
  paid: money(45000),
  lines: [
    { kind: 'accommodation', concept: 'Ático Plaza Mayor · 3 noches', amount: money(45000) },
    { kind: 'paid', concept: 'Ya pagado al reservar', amount: money(-45000) },
  ],
  methods: [
    { id: 'saved', kind: 'saved_card' },
    { id: 'card', kind: 'card' },
  ],
  savedCard: { id: 'saved', last4: '4421', holder: 'MARTA RUIZ', expiry: '09/28', needsCvv: true },
  advance: { amount: money(45000), percent: 100, paid: true, paidAt: '2026-09-01T10:12:00+02:00' },
  rest: null,
  channelValidation: { required: false, channelName: 'Booking.com' },
  freeCancel: true,
  freeCancelUntil: '2026-09-26T23:59:00+02:00',
  rateConditions: 'Cancelación gratuita hasta 7 días antes de la llegada.',
  ids: { standard: 'PT-STD', advance: 'PT-ADV', rest: 'PT-REST', bail: 'PT-BAIL', paymentCard: 'PM-CARD', paymentBizum: null },
}

export const MOCK_PAYMENT_HOTEL: PaymentSummary = {
  total: money(55500),
  pending: money(28500),
  paid: money(27000),
  lines: [
    { kind: 'accommodation', concept: 'Habitación Doble superior · 3 noches', amount: money(54000) },
    { kind: 'paid', concept: 'Ya pagado al reservar (50 %)', amount: money(-27000) },
    { kind: 'tourist_tax', concept: 'Tasa turística · 2 personas × 3 noches', amount: money(1500) },
  ],
  methods: [
    { id: 'saved', kind: 'saved_card' },
    { id: 'card', kind: 'card' },
  ],
  savedCard: { id: 'saved', last4: '4421', holder: 'MARTA RUIZ', expiry: '09/29', needsCvv: false },
  advance: { amount: money(27000), percent: 50, paid: true, paidAt: '2026-09-14T18:40:00+02:00' },
  rest: { amount: money(28500), paid: false, paidAt: null },
  channelValidation: { required: false, channelName: 'Booking.com' },
  freeCancel: false,
  freeCancelUntil: null,
  rateConditions: 'Tarifa no reembolsable.',
  ids: { standard: 'PT-STD', advance: 'PT-ADV', rest: 'PT-REST', bail: null, paymentCard: 'PM-CARD', paymentBizum: null },
}

export const MOCK_TAX: TouristTax = {
  total: money(1500),
  perPersonNight: money(250),
  persons: 2,
  nights: 3,
  exemptPersons: 0,
  exemptions: ['Menores de 16 años.', 'Personas que se alojan por motivos médicos.', 'Trabajadores desplazados con justificante.'],
}

export const MOCK_CHARGES: Charge[] = [
  { id: 'CH-1', concept: 'Desayuno · 3 personas', date: '2026-10-04T08:31:00+02:00', amount: money(3600), receiptSent: true },
  { id: 'CH-2', concept: 'Salir más tarde, hasta las 14:00', date: '2026-10-06T09:02:00+02:00', amount: money(2500), receiptSent: true },
]

const RECEIPT_URL = (id: string) => `https://example.invalid/recibo-${id}.pdf`
export const MOCK_RECEIPTS: Receipt[] = [
  { id: 'RC-1', concept: 'Pago de la reserva (50 %)', date: '2026-09-14T18:40:00+02:00', amount: money(27000), url: RECEIPT_URL('RC-1') },
  { id: 'RC-2', concept: 'Resto y tasa turística', date: '2026-10-03T16:05:00+02:00', amount: money(28500), url: RECEIPT_URL('RC-2') },
  { id: 'RC-3', concept: 'Desayuno', date: '2026-10-04T08:31:00+02:00', amount: money(3600), url: RECEIPT_URL('RC-3') },
]

/** Registro de consentimiento que guardaría el backend (solo mock). */
export const MOCK_CONSENT_FIELD = 'mockInStayConsent'
type MockConsentRecord = InStayConsentInput & { recordedAt: string }

/** Pagos simulados ya cobrados, para que el resumen y la fianza cambien tras pagar en el mock. */
export const MOCK_PAID_FIELD = 'mockPaid'
const paidKinds = (guestCode: string) => readLocal<PaymentKind[]>(guestCode, MOCK_PAID_FIELD) ?? []
const markPaid = (guestCode: string, kind: PaymentKind) => writeLocal(guestCode, MOCK_PAID_FIELD, [...new Set([...paidKinds(guestCode), kind])])

/** Importe cobrado por cambios de reserva en el mock de reserva (céntimos). */
export const MOCK_CHANGE_PAID_FIELD = 'mockChangePaid'
/** Diferencia de un cambio de reserva aplicado y aún sin pagar, como deja `modifyreserve` el `pendingPrice` (D45). */
export const MOCK_CHANGE_PENDING_FIELD = 'mockChangePending'

/** Cobro del pendiente de un cambio (pago "Total"): pasa de pendiente a pagado. true si había algo que mover. */
function settleChangePending(guestCode: string, kind: PaymentKind): boolean {
  const pending = readLocal<number>(guestCode, MOCK_CHANGE_PENDING_FIELD) ?? 0
  if (kind !== 'rest' || pending <= 0) return false
  writeLocal(guestCode, MOCK_CHANGE_PAID_FIELD, (readLocal<number>(guestCode, MOCK_CHANGE_PAID_FIELD) ?? 0) + pending)
  writeLocal(guestCode, MOCK_CHANGE_PENDING_FIELD, null)
  return true
}

/** Decisión de la página simulada del banco, compartida con el iframe por `localStorage` (mismo origen). */
export const MOCK_BANK_PREFIX = 'v2.mock.banco.'
export type MockBankDecision = 'completado' | 'cancelado'
export function writeMockBankDecision(temporalPaidId: string, decision: MockBankDecision): void {
  try {
    localStorage.setItem(MOCK_BANK_PREFIX + temporalPaidId, decision)
  } catch {
    /* sin almacenamiento */
  }
}
export function readMockBankDecision(temporalPaidId: string): MockBankDecision | null {
  try {
    return localStorage.getItem(MOCK_BANK_PREFIX + temporalPaidId) as MockBankDecision | null
  } catch {
    return null
  }
}

interface MockPayment {
  guestCode: string
  kind: PaymentKind
}
const payments = new Map<string, MockPayment>()

function summaryFor(guestCode: string): PaymentSummary {
  let s = guestCode.includes('hotel') ? MOCK_PAYMENT_HOTEL : MOCK_PAYMENT_PAID
  if (guestCode.includes('canal')) s = { ...MOCK_PAYMENT_HOTEL, channelValidation: { required: true, channelName: 'Booking.com' } }
  if (guestCode.includes('sin-tarjeta')) s = { ...s, savedCard: null, methods: s.methods.filter((m) => m.kind !== 'saved_card') }
  // Cambios de reserva cobrados con el mock de reserva (cotizar, cobrar y aplicar): suman al total y a lo pagado.
  const changePaid = readLocal<number>(guestCode, MOCK_CHANGE_PAID_FIELD) ?? 0
  if (changePaid > 0) {
    const c = s.total.currency
    s = {
      ...s,
      total: money(s.total.amount + changePaid, c),
      paid: money(s.paid.amount + changePaid, c),
      lines: [...s.lines, { kind: 'fee', concept: 'Cambio de reserva', amount: money(changePaid, c) }, { kind: 'paid', concept: 'Pagado al cambiar la reserva', amount: money(-changePaid, c) }],
    }
  }
  const changePending = readLocal<number>(guestCode, MOCK_CHANGE_PENDING_FIELD) ?? 0
  if (changePending > 0) {
    const c = s.total.currency
    const pendingTotal = s.pending.amount + changePending
    s = {
      ...s,
      total: money(s.total.amount + changePending, c),
      pending: money(pendingTotal, c),
      lines: [...s.lines, { kind: 'fee', concept: 'Cambio de reserva · pendiente de pago', amount: money(changePending, c) }],
      rest: { amount: money(pendingTotal, c), paid: false, paidAt: null },
    }
  }
  const paid = paidKinds(guestCode)
  if (s.rest && !s.rest.paid && paid.some((k) => k === 'rest' || k === 'channel')) {
    s = { ...s, pending: money(0, s.pending.currency), paid: s.total, rest: { ...s.rest, paid: true, paidAt: new Date().toISOString() }, channelValidation: { ...s.channelValidation, required: false } }
  }
  return s
}

async function settle(temporalPaidId: string, outcome: 'completed' | 'rejected'): Promise<'completed' | 'rejected'> {
  const payment = payments.get(temporalPaidId)
  if (outcome === 'completed' && payment && !settleChangePending(payment.guestCode, payment.kind)) markPaid(payment.guestCode, payment.kind)
  await Promise.resolve()
  return outcome
}

export const pagosMock: PagosService = {
  async getPaymentSummary(stay, _lang, signal) {
    await simulate(stay.guestCode, 'pagos', signal)
    return summaryFor(stay.guestCode)
  },

  async pay(stay, request, _lang, signal) {
    await simulate(stay.guestCode, 'pagos', signal)
    const g = stay.guestCode
    if (g.includes('pago-ya-pagado')) throw new ServiceError('already_paid')
    if (g.includes('pago-falla')) throw new ServiceError('payment_failed', 'card')
    if (g.includes('pago-directo')) {
      if (!settleChangePending(g, request.kind)) markPaid(g, request.kind)
      return { status: 'completed' }
    }
    const temporalPaidId = `TMP-${Date.now()}`
    payments.set(temporalPaidId, { guestCode: g, kind: request.kind })
    const amount = (request.amount.amount / 100).toFixed(2)
    return { status: 'redirect', temporalPaidId, iframeUrl: `/dev/banco?pago=${encodeURIComponent(temporalPaidId)}&importe=${amount}` }
  },

  async waitForResult(stay, temporalPaidId, { signal, timeoutMs = PAYMENT_WAIT_MS } = {}) {
    const g = stay.guestCode
    if (g.includes('pago-tiempo')) {
      await sleep(Math.min(timeoutMs, MOCK_SLOW_MS), signal)
      return 'timeout'
    }
    if (g.includes('pago-rechazado')) {
      await sleep(PAYMENT_POLL_MS, signal)
      return settle(temporalPaidId, 'rejected')
    }
    if (g.includes('pago-ok')) {
      await sleep(PAYMENT_POLL_MS, signal)
      return settle(temporalPaidId, 'completed')
    }
    const deadline = Date.now() + timeoutMs
    for (;;) {
      const decision = readMockBankDecision(temporalPaidId)
      if (decision === 'completado') return settle(temporalPaidId, 'completed')
      if (decision === 'cancelado') return settle(temporalPaidId, 'rejected')
      if (Date.now() + PAYMENT_POLL_MS > deadline) return 'timeout'
      await sleep(PAYMENT_POLL_MS, signal)
    }
  },

  async getSavedCard(stay, lang, signal) {
    const summary = await pagosMock.getPaymentSummary(stay, lang, signal)
    if (!summary.savedCard) return null
    const record = readLocal<MockConsentRecord>(stay.guestCode, MOCK_CONSENT_FIELD)
    const consent: InStayConsent = record
      ? { status: record.granted ? 'granted' : 'revoked', recordedAt: record.recordedAt, textVersion: record.textVersion }
      : stay.guestCode.includes('consentimiento-pendiente')
        ? { status: 'pending_confirmation', recordedAt: null, textVersion: null }
        : { status: 'granted', recordedAt: '2026-10-03T16:12:00+02:00', textVersion: '2026-09-17' }
    return { card: { ...summary.savedCard, holder: 'Marta Ruiz', brand: 'Visa' }, consent }
  },
  // Simula el endpoint de consentimiento (PUT saved-card): guarda el registro completo como lo haría el backend.
  async setInStayConsent(stay, input) {
    await simulate(stay.guestCode, 'pagos')
    if (stay.guestCode.includes('consentimiento-falla')) throw new ServiceError('server', 'mock consent error')
    const record: MockConsentRecord = { ...input, recordedAt: new Date().toISOString() }
    writeLocal(stay.guestCode, MOCK_CONSENT_FIELD, record)
    return { status: input.granted ? 'granted' : 'revoked', recordedAt: record.recordedAt, textVersion: input.textVersion }
  },

  async getCharges(stay, signal) {
    await simulate(stay.guestCode, 'pagos', signal)
    return stay.guestCode.includes('sin-cargos') ? [] : MOCK_CHARGES
  },

  async getTouristTax(stay, signal) {
    await simulate(stay.guestCode, 'pagos', signal)
    if (!stay.guestCode.includes('hotel')) return null
    return stay.guestCode.includes('tasa-exentos') ? { ...MOCK_TAX, exemptPersons: 1, total: money(750) } : MOCK_TAX
  },
  async notifyTaxExemption(stay) {
    await simulate(stay.guestCode, 'pagos')
    return { sentAt: new Date().toISOString() }
  },

  async getReceipts(stay, signal) {
    await simulate(stay.guestCode, 'pagos', signal)
    return stay.guestCode.includes('sin-recibos') ? [] : MOCK_RECEIPTS
  },
  async resendReceipts(stay, email) {
    await simulate(stay.guestCode, 'pagos')
    return { sentTo: email }
  },

  async getInvoiceInfo(stay, signal) {
    await simulate(stay.guestCode, 'pagos', signal)
    return { bills: stay.flags.showGetBills ? stay.bills : [], canRequest: stay.phase === 'after', fiscalDeadline: stay.dates.checkOut, saved: readLocal(stay.guestCode, FISCAL_FIELD) }
  },
  async saveFiscalData(stay, data) {
    await simulate(stay.guestCode, 'pagos')
    writeLocal(stay.guestCode, FISCAL_FIELD, data)
  },
  async requestInvoice(stay, data) {
    await simulate(stay.guestCode, 'pagos')
    if (stay.guestCode.includes('factura-falla')) throw new ServiceError('server', 'mock invoice error')
    writeLocal(stay.guestCode, FISCAL_FIELD, data)
    return [{ id: 'F-2026-0412', url: 'https://example.invalid/factura-2026-0412.pdf' }]
  },

  async getProvinces(stay, countryId, signal) {
    await simulate(stay.guestCode, 'pagos', signal)
    if (countryId !== 'ES') return []
    return [
      { id: 'P08', name: 'Barcelona', value: 'Barcelona' },
      { id: 'P28', name: 'Madrid', value: 'Madrid' },
      { id: 'P46', name: 'Valencia', value: 'Valencia' },
    ]
  },
}

/** Para pruebas: olvida los pagos simulados de una reserva. */
export function resetMockPayments(stay: StayRef): void {
  writeLocal(stay.guestCode, MOCK_PAID_FIELD, null)
  payments.clear()
}
